const dayMap = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const dayOrder = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const dayIndexMap = Object.fromEntries(dayOrder.map((day, index) => [day, index]));

const toMinutes = time => {
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
};

const fromMinutes = minutes =>
    `${Math.floor(minutes / 60).toString().padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}`;

const normalizeTime = time => time.slice(0, 5);

/**
 * Converts an array of time intervals into a normalized format.
 * @param {Array} planningList - A list of time intervals with days and times.
 * @returns {Array<string>} Normalized strings representing working hours.
 */
export function parseOpeningHours(planningList) {
    const hoursSet = new Set();

    for (const slot of planningList) {
        if (!slot?.week_days || !slot?.start_time || !slot?.end_time) continue;

        const days = slot.week_days
            .split(",")
            .map(d => dayMap[+d])
            .filter(Boolean)
            .join(",");

        const time = `${normalizeTime(slot.start_time)}-${normalizeTime(slot.end_time)}`;
        hoursSet.add(`${days} ${time}`);
    }

    return normalizeOpeningHours([...hoursSet]);
}

function parseInterval(entry) {
    const [daysPart, timePart] = entry.trim().split(" ");
    if (!daysPart || !timePart || !timePart.includes("-")) return [];

    const [from, to] = timePart.split("-");
    return daysPart.split(",").map(day => ({ day, from, to }));
}

function mergeTimeRanges(ranges) {
    if (!Array.isArray(ranges)) return [];

    const sorted = [...ranges].sort((a, b) => toMinutes(a.from) - toMinutes(b.from));
    const merged = [];

    for (const current of sorted) {
        const last = merged[merged.length - 1];
        if (!last || toMinutes(current.from) > toMinutes(last.to)) {
            merged.push({ ...current });
        } else {
            last.to = fromMinutes(Math.max(toMinutes(last.to), toMinutes(current.to)));
        }
    }

    return merged;
}

function normalizeOpeningHours(openingHours) {
    const intervalsByDay = {};

    openingHours.flatMap(parseInterval).forEach(({ day, from, to }) => {
        if (!intervalsByDay[day]) intervalsByDay[day] = [];
        intervalsByDay[day].push({ from, to });
    });

    for (const day in intervalsByDay) {
        intervalsByDay[day] = mergeTimeRanges(intervalsByDay[day]);
    }

    const daysByTime = {};

    for (const [day, intervals] of Object.entries(intervalsByDay)) {
        for (const { from, to } of intervals) {
            const timeKey = `${from}-${to}`;
            if (!daysByTime[timeKey]) daysByTime[timeKey] = new Set();
            daysByTime[timeKey].add(day);
        }
    }

    return Object.entries(daysByTime).map(([time, daysSet]) => {
        const sortedDays = Array.from(daysSet).sort(
            (a, b) => dayIndexMap[a] - dayIndexMap[b]
        );
        return `${sortedDays.join(",")} ${time}`;
    });
}

/**
 * Translates a string of days (e.g., "Mo,Tu") using t("days.Mo")
 * @param {string} daysStr - A string like "Mo,Tu"
 * @param {(key: string) => string} t - translation function from next-i18next
 * @returns {string} - Translated days, e.g., "Mon, Tue"
 */
export function translateDays(daysStr, t = (s) => s) {
    return daysStr
        .split(",")
        .map((day) => t(`days.${day.trim()}`))
        .join(", ");
}
